// Formulario único de la invitación por correo (captacion-devocional-y-boletin.md, Fase 2).
// Lo usan la franja/sección (NewsletterStrip), el bloque de /devocionales/, la tarjeta
// flotante (SubscribeCard) y la página /newsletter/. Un solo lugar decide qué ve cada
// estado de la persona — ver useSubscriptionState.
//
// Una sola decisión (Víctor, 2026-09-29): la promesa es "devocionales diarios y noticias del
// ministerio", y enviar el formulario pide las dos listas. Sin casillas — ni marcada ni
// desmarcada. El consentimiento sigue siendo explícito porque el texto dice exactamente qué
// llega, y cada correo trae la baja de su propia lista: quien no quiera las noticias las deja
// sin perder el devocional.

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthProvider";
import { useSubscriptionState } from "../../hooks/useSubscriptionState";
import {
  subscribeToNewsletter,
  setMyNewsletterSubscription,
  getSiteLanguage,
} from "../../services/newsletter-service";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function CheckIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" className={className} aria-hidden="true">
      <path d="M4.5 10.5l3.5 3.5 7.5-8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function SubscribedLine({ className = "" }) {
  const { t } = useTranslation();
  return (
    <p className={`animate-fade-in-sm inline-flex items-center justify-center gap-2 text-sm font-semibold text-brand-forest dark:text-brand-parchment ${className}`}>
      <CheckIcon className="w-4 h-4 text-brand-forest dark:text-brand-gold" />
      {t("subscribe.subscribedLine")}
    </p>
  );
}

/**
 * @param source   'strip' | 'home' | 'devotional' | 'card' | 'page' — para medir qué punto
 *                 de captación funciona (lo guarda el servidor).
 * @param cta      'gold' (el CTA principal de esa pantalla) | 'forest' (cuando la pantalla
 *                 ya tiene su dorado — regla de un solo dorado).
 * @param compact  versión de la tarjeta flotante: todo más chico, sin nota de privacidad.
 * @param align    'center' | 'left'
 */
export default function SubscribeForm({ source = "strip", cta = "gold", compact = false, align = "center" }) {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [subState, setSubState] = useSubscriptionState();
  const [email, setEmail] = useState("");
  // idle | sending | already | authDone | invalidEmail | error
  const [status, setStatus] = useState("idle");
  // "¿No te llegó?" vuelve a mostrar el formulario aunque la marca siga en 'pending'.
  const [retrying, setRetrying] = useState(false);

  const center = align === "center";
  const ctaClasses =
    cta === "gold"
      ? "bg-brand-gold hover:bg-brand-gold-dark text-brand-forest"
      : "bg-brand-forest hover:bg-brand-dark text-brand-parchment";
  const lists = ["devotional", "newsletter"];

  if (status === "authDone") {
    return (
      <p className={`animate-fade-in-sm text-base font-bold text-brand-forest dark:text-brand-parchment ${center ? "text-center" : ""}`}>
        {t("subscribe.authSuccess")}
      </p>
    );
  }

  if (subState === "subscribed" || status === "already") {
    return (
      <div className={center ? "text-center" : ""}>
        <SubscribedLine />
        {status === "already" && (
          <p className="mt-1 text-xs text-brand-forest/70 dark:text-brand-parchment/60">{t("subscribe.alreadyBody")}</p>
        )}
      </div>
    );
  }

  if (subState === "pending" && !retrying) {
    return (
      <div className={`animate-fade-in-sm ${center ? "text-center" : ""}`}>
        <p className={`font-bold text-brand-forest dark:text-brand-parchment ${compact ? "text-base" : "text-lg"} mb-1`}>
          {t("subscribe.pendingTitle")}
        </p>
        <p className="text-sm text-brand-forest/80 dark:text-brand-parchment/75">{t("subscribe.pendingBody")}</p>
        {!user && (
          <button
            type="button"
            onClick={() => {
              setRetrying(true);
              setStatus("idle");
            }}
            className="mt-3 text-sm font-semibold underline underline-offset-4 text-brand-forest dark:text-brand-parchment hover:text-brand-gold-dark"
          >
            {t("subscribe.pendingRetry")}
          </button>
        )}
      </div>
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    if (!EMAIL_RE.test(value)) {
      setStatus("invalidEmail");
      return;
    }
    setStatus("sending");
    try {
      const result = await subscribeToNewsletter(value, getSiteLanguage(), source, lists);
      if (result?.alreadyConfirmed) {
        setSubState("subscribed");
        setStatus("already");
      } else {
        setSubState("pending");
        setRetrying(false);
        setStatus("idle");
      }
    } catch (err) {
      setStatus(err?.code === "functions/invalid-argument" ? "invalidEmail" : "error");
    }
  }

  // Con sesión: el correo de la cuenta ya está verificado, entra directo sin confirmación.
  async function handleAuthSubmit() {
    setStatus("sending");
    try {
      await setMyNewsletterSubscription(true, "devotional");
      await setMyNewsletterSubscription(true, "newsletter");
      setSubState("subscribed");
      setStatus("authDone");
    } catch (err) {
      setStatus("error");
    }
  }

  const errorLine = (status === "error" || status === "invalidEmail") && (
    <p className={`text-xs text-red-700 dark:text-red-400 ${center ? "text-center" : ""}`}>
      {status === "invalidEmail" ? t("subscribe.invalidEmail") : t("subscribe.genericError")}
    </p>
  );

  // Mientras la sesión se resuelve se muestra la versión de visitante: es lo que trae el HTML
  // estático, y la mayoría de quien llega no tiene cuenta (mismo criterio que S16).
  if (user && !loading) {
    return (
      <div className={`space-y-4 ${center ? "text-center" : ""}`}>
        <p className="text-sm text-brand-forest/80 dark:text-brand-parchment/75 break-words">
          {t("subscribe.authNote", { email: user.email })}
        </p>
        <button
          type="button"
          onClick={handleAuthSubmit}
          disabled={status === "sending"}
          className={`inline-flex items-center justify-center font-bold text-sm uppercase tracking-wide px-8 py-3 rounded-full transition-colors duration-200 disabled:opacity-50 ${ctaClasses}`}
        >
          {status === "sending" ? t("subscribe.sending") : t("subscribe.submit")}
        </button>
        {errorLine}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={compact ? "space-y-3" : "space-y-4"}>
      <div
        className={`flex ${compact ? "flex-col gap-2" : "flex-col sm:flex-row gap-3"} items-stretch ${
          center ? "justify-center max-w-lg mx-auto" : ""
        }`}
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status !== "idle" && status !== "sending") setStatus("idle");
          }}
          placeholder={t("subscribe.emailPlaceholder")}
          aria-label={t("subscribe.emailPlaceholder")}
          className={`flex-1 min-w-0 px-4 ${compact ? "py-2.5" : "py-3"} rounded-full border border-brand-gold/30 dark:border-brand-forest bg-white dark:bg-brand-forest/40 text-brand-forest dark:text-brand-parchment placeholder-brand-haze text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold transition`}
        />
        <button
          type="submit"
          disabled={status === "sending" || !email.trim()}
          className={`inline-flex items-center justify-center font-bold text-sm uppercase tracking-wide px-7 ${compact ? "py-2.5" : "py-3"} rounded-full transition-colors duration-200 disabled:opacity-50 whitespace-nowrap ${ctaClasses}`}
        >
          {status === "sending" ? t("subscribe.sending") : t("subscribe.submit")}
        </button>
      </div>
      {errorLine}
    </form>
  );
}
